/**
 * The rich-text extension list (architecture §11.1): ONE schema for the browser editor (F11) and the server
 * (`validateDoc`). Integrator-owned after F04 and amended in wave step 0 (slices.yaml F11: "the server-side node list
 * lives in packages/editor/src/schema/extensions.ts"). TipTap 3.31, MIT packages only.
 *
 * StarterKit is configured explicitly: v3 also enables underline, listKeymap, trailingNode, dropcursor and gapcursor;
 * underline is off so the schema and `validateDoc` stay the same set. The browser swaps StarterKit's `codeBlock` for
 * `CodeBlockLowlight` (same node spec, our incremental highlighter, F11) and adds client-only extensions (placeholder,
 * suggestion, node views); none of them changes the schema. P1 adds `attachment { fileId }` and tables in step 0.
 */
import { Node, mergeAttributes, type AnyExtension } from '@tiptap/core';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import Mention from '@tiptap/extension-mention';
import StarterKit from '@tiptap/starter-kit';
import { SCHEMA_HEADING_LEVELS } from '../command-table';

export const MENTION_KINDS = ['user', 'issue', 'project', 'document'] as const;

/** `el.getAttribute` without the DOM lib (the schema module is isomorphic; parseHTML only runs in the browser). */
const attr = (el: unknown, name: string): string | null => (el as { getAttribute(n: string): string | null }).getAttribute(name);
export type MentionKind = (typeof MENTION_KINDS)[number];

/**
 * `mention { kind, id, inert }`: labels are never stored (rendered from the store at display time, §11.5); `inert`
 * marks a user mention of someone who couldn't see the entity when it was added (no row, notification or
 * subscription; ADR-0040 decision 8).
 */
export const QuadraticMention = Mention.extend({
  addAttributes() {
    return {
      kind: { default: 'user', parseHTML: (el) => attr(el, 'data-kind'), renderHTML: (a: { kind: string }) => ({ 'data-kind': a.kind }) },
      id: { default: null, parseHTML: (el) => attr(el, 'data-id'), renderHTML: (a: { id: string | null }) => ({ 'data-id': a.id }) },
      inert: {
        default: false,
        parseHTML: (el) => attr(el, 'data-inert') === 'true',
        renderHTML: (a: { inert: boolean }) => (a.inert ? { 'data-inert': 'true' } : {}),
      },
    };
  },
});

/** `image { fileId }`: an uploaded file bound to the containing entity (§10.4 binding rule). */
export const QuadraticImage = Node.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      fileId: { default: null, parseHTML: (el) => attr(el, 'data-file-id'), renderHTML: (a: { fileId: string | null }) => ({ 'data-file-id': a.fileId }) },
    };
  },
  parseHTML() {
    return [{ tag: 'img[data-file-id]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },
});

/** The schema's extensions (nodes and marks plus StarterKit's behaviour extensions, which add no schema). */
export function schemaExtensions(): AnyExtension[] {
  return [
    StarterKit.configure({
      underline: false,
      heading: { levels: [...SCHEMA_HEADING_LEVELS] as (1 | 2 | 3 | 4 | 5 | 6)[] },
      codeBlock: { defaultLanguage: 'plaintext' },
      link: { openOnClick: false, autolink: true, protocols: ['http', 'https', 'mailto'] },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    QuadraticMention,
    QuadraticImage,
  ];
}

/**
 * The code-block language allowlist (40; registry issues.editor.code-blocks): lowlight's `common` grammars plus a
 * few. `plaintext` is the default and is never auto-detected.
 */
export const CODE_LANGUAGES = [
  'plaintext',
  'arduino',
  'bash',
  'c',
  'cpp',
  'csharp',
  'css',
  'diff',
  'dockerfile',
  'elixir',
  'go',
  'graphql',
  'haskell',
  'ini',
  'java',
  'javascript',
  'json',
  'kotlin',
  'less',
  'lua',
  'makefile',
  'markdown',
  'objectivec',
  'perl',
  'php',
  'python',
  'r',
  'ruby',
  'rust',
  'scala',
  'scss',
  'shell',
  'sql',
  'swift',
  'typescript',
  'vbnet',
  'wasm',
  'xml',
  'yaml',
  'toml',
] as const;
